import { ethers } from 'ethers';
import { readContract, writeContract, prepareWriteContract } from '@wagmi/core';
import { CHAIN_CONFIGS, ERC20_ABI, BRIDGE_ABI, DEFAULT_GAS_LIMIT } from './constants';
import { ChainConfig, TokenType } from '../types';

// Global provider cache with wallet-specific tracking
let cachedProviders: Map<string, ethers.providers.Web3Provider> = new Map();

// Get wallet identifier for caching
const getWalletId = (): string => {
  if (typeof window === 'undefined' || !window.ethereum) return 'unknown';
  
  // Check for specific wallet identifiers
  if (window.ethereum.isMetaMask) return 'metamask';
  if (window.ethereum.isKeplr) return 'keplr';
  if (window.ethereum.isCosmostation) return 'cosmostation';
  if (window.ethereum.isRabby) return 'rabby';
  if (window.ethereum.isCoinbaseWallet) return 'coinbase';
  
  // Fallback to a generic identifier
  return window.ethereum.constructor?.name || 'generic';
};

// Initialize provider
export const getProvider = async (_chainId: string): Promise<ethers.providers.Web3Provider | null> => {
  if (typeof window === 'undefined') {
    console.error('Window is undefined');
    return null;
  }

  try {
    const walletId = getWalletId();
    
    // Return cached provider for this specific wallet if available
    if (cachedProviders.has(walletId)) {
      return cachedProviders.get(walletId)!;
    }

    let ethereumProvider = window.ethereum;

    // Special handling for Keplr and Cosmostation EVM mode
    if (window.keplr && walletId === 'keplr') {
      // For Keplr, try to get the EVM provider
      ethereumProvider = window.ethereum || window.keplr?.ethereum;
    } else if (window.cosmostation && walletId === 'cosmostation') {
      // For Cosmostation, try to get the EVM provider
      ethereumProvider = window.ethereum || window.cosmostation?.ethereum;
    }

    if (!ethereumProvider) {
      console.error('No ethereum provider found for wallet:', walletId);
      return null;
    }

    const provider = new ethers.providers.Web3Provider(ethereumProvider);
    
    // Don't automatically request accounts - let Web3Modal/Wagmi handle wallet connection
    // Just create the provider and let the user connect through the UI
    
    // Cache the provider for this specific wallet
    cachedProviders.set(walletId, provider);
    return provider;
  } catch (error) {
    console.error('Error initializing provider:', error);
    return null;
  }
};

// Create provider from Wagmi's wallet client
export const getProviderFromWagmi = (walletClient: any): ethers.providers.Web3Provider | null => {
  try {
    if (!walletClient || !walletClient.transport) {
      console.error('Invalid wallet client');
      return null;
    }

    // Create provider from Wagmi's transport
    const provider = new ethers.providers.Web3Provider(walletClient.transport);
    return provider;
  } catch (error) {
    console.error('Error creating provider from Wagmi:', error);
    return null;
  }
};

// Clear provider cache (useful when switching wallets)
export const clearProviderCache = () => {
  cachedProviders.clear();
};

// Switch network
export const switchNetwork = async (chainId: string): Promise<boolean> => {
  if (!window.ethereum) return false;

  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId }],
    });
    return true;
  } catch (switchError: any) {
    // This error code indicates that the chain has not been added to MetaMask
    if (switchError.code === 4902) {
      try {
        const chainConfig = Object.values(CHAIN_CONFIGS).find(
          (config) => config.chainId === chainId
        );
        
        if (!chainConfig) {
          throw new Error(`Chain configuration not found for chainId: ${chainId}`);
        }

        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId,
              chainName: chainConfig.name,
              nativeCurrency: chainConfig.nativeCurrency || {
                name: 'ETH',
                symbol: 'ETH',
                decimals: 18,
              },
              rpcUrls: [chainConfig.rpcUrl],
              blockExplorerUrls: chainConfig.explorerUrl ? [chainConfig.explorerUrl] : undefined,
            },
          ],
        });
        return true;
      } catch (addError) {
        console.error('Error adding chain:', addError);
        return false;
      }
    }
    console.error('Error switching chain:', switchError);
    return false;
  }
};

// Get token balance using Wagmi (works with all wallets)
export const getTokenBalanceWagmi = async (
  tokenAddress: string,
  userAddress: string
): Promise<string> => {
  try {
    // Validate token address
    if (!tokenAddress || tokenAddress === '' || !ethers.utils.isAddress(tokenAddress)) {
      console.warn('Invalid token address:', tokenAddress);
      return '0';
    }

    const balance = await readContract({
      address: tokenAddress as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'balanceOf',
      args: [userAddress],
    });

    const decimals = await readContract({
      address: tokenAddress as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'decimals',
    });

    return ethers.utils.formatUnits(balance as any, decimals as any);
  } catch (error) {
    console.error('Error getting token balance with Wagmi:', error);
    return '0';
  }
};

// Get token balance (legacy function for compatibility)
export const getTokenBalance = async (
  tokenAddress: string,
  userAddress: string,
  provider: ethers.providers.Web3Provider
): Promise<string> => {
  try {
    // Validate token address
    if (!tokenAddress || tokenAddress === '' || !ethers.utils.isAddress(tokenAddress)) {
      console.warn('Invalid token address:', tokenAddress);
      return '0';
    }

    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
    const balance = await tokenContract.balanceOf(userAddress);
    const decimals = await tokenContract.decimals();
    return ethers.utils.formatUnits(balance, decimals);
  } catch (error) {
    console.error('Error getting token balance:', error);
    return '0';
  }
};

// Check token allowance using Wagmi (works with all wallets)
export const checkAllowanceWagmi = async (
  tokenAddress: string,
  userAddress: string,
  spenderAddress: string
): Promise<string> => {
  try {
    // Validate token address
    if (!tokenAddress || tokenAddress === '' || !ethers.utils.isAddress(tokenAddress)) {
      console.warn('Invalid token address for allowance check:', tokenAddress);
      return '0';
    }

    const allowance = await readContract({
      address: tokenAddress as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'allowance',
      args: [userAddress, spenderAddress],
    });

    const decimals = await readContract({
      address: tokenAddress as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'decimals',
    });

    return ethers.utils.formatUnits(allowance as any, decimals as any);
  } catch (error) {
    console.error('Error checking allowance with Wagmi:', error);
    return '0';
  }
};

// Check token allowance (legacy function for compatibility)
export const checkAllowance = async (
  tokenAddress: string,
  userAddress: string,
  spenderAddress: string,
  provider: ethers.providers.Web3Provider
): Promise<string> => {
  try {
    // Validate token address
    if (!tokenAddress || tokenAddress === '' || !ethers.utils.isAddress(tokenAddress)) {
      console.warn('Invalid token address for allowance check:', tokenAddress);
      return '0';
    }

    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
    const allowance = await tokenContract.allowance(userAddress, spenderAddress);
    const decimals = await tokenContract.decimals();
    return ethers.utils.formatUnits(allowance, decimals);
  } catch (error) {
    console.error('Error checking allowance:', error);
    return '0';
  }
};

// Approve token using Wagmi (works with all wallets)
export const approveTokenWagmi = async (
  tokenAddress: string,
  spenderAddress: string,
  amount: string
): Promise<string> => {
  try {
    const decimals = await readContract({
      address: tokenAddress as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'decimals',
    });

    const parsedAmount = ethers.utils.parseUnits(amount, decimals as any);

    const config = await prepareWriteContract({
      address: tokenAddress as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'approve',
      args: [spenderAddress, parsedAmount],
    });

    const { hash } = await writeContract(config);
    return hash;
  } catch (error) {
    console.error('Error approving token with Wagmi:', error);
    throw error;
  }
};

// Approve token (legacy function for compatibility)
export const approveToken = async (
  tokenAddress: string,
  spenderAddress: string,
  amount: string,
  provider: ethers.providers.Web3Provider
): Promise<string> => {
  try {
    const signer = provider.getSigner();
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
    const decimals = await tokenContract.decimals();
    const parsedAmount = ethers.utils.parseUnits(amount, decimals);
    
    const tx = await tokenContract.approve(spenderAddress, parsedAmount);
    await tx.wait();
    return tx.hash;
  } catch (error) {
    console.error('Error approving token:', error);
    throw error;
  }
};

// Bridge token using Wagmi (works with all wallets)
export const bridgeTokenWagmi = async (
  fromChainConfig: ChainConfig,
  toChainConfig: ChainConfig,
  tokenType: TokenType,
  amount: string,
  recipientAddress: string,
  feeData?: {
    fee: string;
    deadline: number;
    signature: string;
  }
): Promise<string> => {
  try {
    const tokenAddress = fromChainConfig[tokenType];
    
    if (!tokenAddress) {
      throw new Error(`${tokenType} not supported on ${fromChainConfig.name}`);
    }
    
    // Get the actual token decimals from the source chain
    let decimals;
    if (tokenAddress === ethers.constants.AddressZero) {
      // Native token
      decimals = fromChainConfig.nativeCurrency?.decimals || 18;
    } else {
      // For BSC, USDC has 18 decimals; for other chains, USDC has 6 decimals
      if (fromChainConfig.chainId === '0x61') { // BSC Testnet
        decimals = 18;
      } else {
        decimals = 6;
      }
    }
    
    const parsedAmount = ethers.utils.parseUnits(amount, decimals);

    if (!feeData) {
      throw new Error('Fee data is required for cross-chain transactions');
    }
    
    // Convert fee to proper format - use the same decimals as the amount (source chain format)
    const parsedFee = ethers.utils.parseUnits(feeData.fee, decimals);
    
    // Determine direction and use appropriate function
    const isFromShido = fromChainConfig.chainId === '0x232f'; // Shido testnet
    const isToShido = toChainConfig.chainId === '0x232f'; // Shido testnet
    
    let config;
    if (isFromShido && !isToShido) {
      // FROM Shido TO other chains: use crossBack with wrapped tokens
      config = await prepareWriteContract({
        address: fromChainConfig.bridge as `0x${string}`,
        abi: BRIDGE_ABI,
        functionName: 'crossBack',
        args: [
          tokenAddress,
          toChainConfig.bip44ChainId,
          recipientAddress,
          parsedAmount,
          DEFAULT_GAS_LIMIT,
          parsedFee,
          feeData.deadline,
          feeData.signature
        ],
      });
    } else if (!isFromShido && isToShido) {
      // FROM other chains TO Shido: use crossTo with native tokens
      config = await prepareWriteContract({
        address: fromChainConfig.bridge as `0x${string}`,
        abi: BRIDGE_ABI,
        functionName: 'crossTo',
        args: [
          tokenAddress,
          toChainConfig.bip44ChainId,
          recipientAddress,
          parsedAmount,
          DEFAULT_GAS_LIMIT,
          parsedFee,
          feeData.deadline,
          feeData.signature
        ],
        value: tokenAddress === ethers.constants.AddressZero ? parsedAmount : undefined,
      });
    } else {
      throw new Error('Invalid bridge direction. Please bridge between Shido and other supported chains.');
    }
    
    const { hash } = await writeContract(config);
    return hash;
  } catch (error) {
    console.error('Error bridging token with Wagmi:', error);
    throw error;
  }
};

// Bridge token (legacy function for compatibility)
export const bridgeToken = async (
  fromChainConfig: ChainConfig,
  toChainConfig: ChainConfig,
  tokenType: TokenType,
  amount: string,
  recipientAddress: string,
  provider: ethers.providers.Web3Provider,
  feeData?: {
    fee: string;
    deadline: number;
    signature: string;
  }
): Promise<string> => {
  try {
    const signer = provider.getSigner();
    const bridgeContract = new ethers.Contract(fromChainConfig.bridge, BRIDGE_ABI, signer);
    const tokenAddress = fromChainConfig[tokenType];
    
    if (!tokenAddress) {
      throw new Error(`${tokenType} not supported on ${fromChainConfig.name}`);
    }
    
    // Get the actual token decimals from the source chain
    let decimals;
    if (tokenAddress === ethers.constants.AddressZero) {
      // Native token
      decimals = fromChainConfig.nativeCurrency?.decimals || 18;
    } else {
      // For BSC, USDC has 18 decimals; for other chains, USDC has 6 decimals
      if (fromChainConfig.chainId === '0x61') { // BSC Testnet
        decimals = 18;
      } else {
        decimals = 6;
      }
    }
    
    const parsedAmount = ethers.utils.parseUnits(amount, decimals);
    

    if (!feeData) {
      throw new Error('Fee data is required for cross-chain transactions');
    }
    
    // Convert fee to proper format - use the same decimals as the amount (source chain format)
    const parsedFee = ethers.utils.parseUnits(feeData.fee, decimals);
    
    // Determine direction and use appropriate function
    const isFromShido = fromChainConfig.chainId === '0x232f'; // Shido testnet
    const isToShido = toChainConfig.chainId === '0x232f'; // Shido testnet
    
    let tx;
    if (isFromShido && !isToShido) {
      // FROM Shido TO other chains: use crossBack with wrapped tokens
      tx = await bridgeContract.crossBack(
        tokenAddress,
        toChainConfig.bip44ChainId,
        recipientAddress,
        parsedAmount,
        DEFAULT_GAS_LIMIT,
        parsedFee,
        feeData.deadline,
        feeData.signature
      );
    } else if (!isFromShido && isToShido) {
      // FROM other chains TO Shido: use crossTo with native tokens
      tx = await bridgeContract.crossTo(
        tokenAddress,
        toChainConfig.bip44ChainId,
        recipientAddress,
        parsedAmount,
        DEFAULT_GAS_LIMIT,
        parsedFee,
        feeData.deadline,
        feeData.signature,
        { value: tokenAddress === ethers.constants.AddressZero ? parsedAmount : 0 }
      );
    } else {
      throw new Error('Invalid bridge direction. Please bridge between Shido and other supported chains.');
    }
    
    await tx.wait();
    return tx.hash;
  } catch (error) {
    console.error('Error bridging token:', error);
    throw error;
  }
};

// Estimate bridge fee
export const estimateBridgeFee = async (
  fromChainConfig: ChainConfig,
  toChainConfig: ChainConfig,
  provider: ethers.providers.Web3Provider
): Promise<string> => {
  try {

    // Get current gas price
    const gasPrice = await provider.getGasPrice();
    
    // Estimate gas for the transaction (typical bridge transaction uses around 150,000-200,000 gas)
    const estimatedGas = ethers.BigNumber.from(200000);
    
    // Calculate fee based on gas price and estimated gas
    const baseFee = gasPrice.mul(estimatedGas);
    
    // Add a small buffer (20%) to ensure enough funds for the transaction
    const bufferedFee = baseFee.mul(120).div(100);
    
    // Convert to ETH
    const feeInEth = ethers.utils.formatEther(bufferedFee);
    
    return feeInEth;
  } catch (error) {
    console.error('Error estimating bridge fee:', error);
    return '0.001'; // Default small fee instead of 0
  }
};

// Check cross-chain message status with destination transaction hash
export const checkCrossChainMessageStatus = async (
  txHash: string
): Promise<{
  status: 'pending' | 'processing' | 'completed' | 'failed';
  toChainTxHash?: string;
}> => {
  try {
    // Use the Bridge API to check the cross-chain message status
    const apiUrl = `https://bridge-api.wanchain.org/api/testnet/status/msg/${txHash}`;
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    // The API returns an array of objects
    if (Array.isArray(data) && data.length > 0) {
      const txData = data[0];
      
      if (txData.status) {
        // Make sure we handle the status case-insensitively
        const status = String(txData.status).toLowerCase();
        
        // Map the API status to our status types
        if (status === 'success' || status === 'confirmed') {
          return {
            status: 'completed',
            toChainTxHash: txData.receiveTxHash
          };
        } else if (status === 'failed' || status === 'rejected' || status === 'error') {
          return { status: 'failed' };
        } else if (status === 'processing') {
          return { status: 'processing' };
        } else if (status === 'pending') {
          return { status: 'pending' };
        }
      }
    }
    
    // If we couldn't determine the status or it's still in progress
    return { status: 'pending' };
  } catch (error) {
    console.error('Error checking cross-chain message status:', error);
    // If there's an error, we assume the transaction is still pending
    return { status: 'pending' };
  }
};

// Legacy function for backward compatibility
export const checkCrossChainMessageStatusLegacy = async (
  txHash: string
): Promise<'pending' | 'processing' | 'completed' | 'failed'> => {
  const result = await checkCrossChainMessageStatus(txHash);
  return result.status;
};

// Check transaction status
export const checkTransactionStatus = async (
  txHash: string,
  chainId: string
): Promise<'pending' | 'processing' | 'completed' | 'failed'> => {
  try {
    const provider = await getProvider(chainId);
    if (!provider) throw new Error('Failed to initialize provider');
    
    const receipt = await provider.getTransactionReceipt(txHash);
    
    if (!receipt) {
      return 'pending';
    }
    
    return receipt.status ? 'completed' : 'failed';
  } catch (error) {
    console.error('Error checking transaction status:', error);
    return 'pending';
  }
};

// Format address for display
export const formatAddress = (address: string): string => {
  if (!address) return '';
  return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
};

// Get transaction URL for explorer
export const getExplorerTxUrl = (chainId: string, txHash: string): string => {
  const chainConfig = Object.values(CHAIN_CONFIGS).find(
    (config) => config.chainId === chainId
  );
  
  if (!chainConfig || !chainConfig.explorerUrl) return '';
  
  return `${chainConfig.explorerUrl}/tx/${txHash}`;
};
